import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { Chip, ListItemText, Stack, styled, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { FC, useCallback } from 'react';
import { auditLogDetailsVar, auditLogDrawerOpenVar } from 'src/infrastructure/local_state';
import { prettyNameForAuditLogFieldName } from 'src/utils/audit-log/formatters/change-formatter';
import { AuditLogListItem, BaseAuditLogDrawer } from '../BaseAuditLogDrawer/BaseAuditLogDrawer';

export const AuditLogDetailsDrawer: FC = () => {
    const [drawerOpen, setDrawerOpen] = useReactiveVar(auditLogDrawerOpenVar);
    const [{ loading, error, details }] = useReactiveVar(auditLogDetailsVar);

    const closeDrawer = useCallback(() => {
        setDrawerOpen(false);
    }, [setDrawerOpen]);

    const sortedDetails = Array.from(details.entries())
        .map(([date, users]) => ({ timestamp: parseISO(date).getTime(), date, users }))
        .toSorted((a, b) => b.timestamp - a.timestamp);

    return (
        <BaseAuditLogDrawer
            onClose={closeDrawer}
            open={drawerOpen}
            error={error}
            loading={loading}
            isEmpty={!loading && details.size <= 0}
        >
            <Stack
                sx={{
                    flex: 1,
                    gap: 2,
                    overflow: 'auto',
                    width: 475,
                }}
            >
                {sortedDetails.map(({ date, users }) => {
                    return (
                        <AuditLogListItem key={date}>
                            <ListItemText
                                primary={
                                    <Typography variant="subtitle1">
                                        {format(new Date(date), 'LLLL d, yyyy - HH:mm:ss')}
                                    </Typography>
                                }
                                secondary={
                                    <Stack
                                        sx={{
                                            mt: 1,
                                            gap: 8,
                                        }}
                                    >
                                        {Array.from(users.entries()).map(([userName, changes]) => {
                                            return (
                                                <Stack key={userName}>
                                                    <Stack
                                                        sx={{
                                                            gap: 0,
                                                        }}
                                                    >
                                                        <Typography variant="caption">
                                                            {userName} made{' '}
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                }}
                                                            >
                                                                {changes.length}
                                                            </Typography>{' '}
                                                            change
                                                            {changes.length > 1 ? 's' : ''}
                                                        </Typography>
                                                        <Stack
                                                            sx={{
                                                                mt: 2,
                                                                gap: 2,
                                                            }}
                                                        >
                                                            {changes.map((change) => (
                                                                <Stack
                                                                    direction="row"
                                                                    key={change.fieldName}
                                                                    sx={{
                                                                        gap: 1,
                                                                        alignItems: 'center',
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        sx={{ fontSize: '11px' }}
                                                                        label={prettyNameForAuditLogFieldName(
                                                                            change.fieldName
                                                                        )}
                                                                    />
                                                                    <Tooltip title={change.formattedOldValue}>
                                                                        <OverflowChip
                                                                            label={change.formattedOldValue}
                                                                            variant="outlined"
                                                                        />
                                                                    </Tooltip>
                                                                    &rarr;
                                                                    <Tooltip title={change.formattedNewValue}>
                                                                        <OverflowChip
                                                                            label={change.formattedNewValue}
                                                                            variant="outlined"
                                                                        />
                                                                    </Tooltip>
                                                                </Stack>
                                                            ))}
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                }
                                slotProps={{
                                    secondary: { component: 'div' },
                                }}
                            />
                        </AuditLogListItem>
                    );
                })}
            </Stack>
        </BaseAuditLogDrawer>
    );
};

const OverflowChip = styled(Chip)({
    minWidth: 16,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
});
